import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIeKitLabels } from '@/hooks/useIeKitLabels';
import { useLocationCategoryLabels } from '@/hooks/useLocationCategoryLabels';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { IssuePlannerTable } from './IssuePlannerTable';
import type { LocationOption } from './IssuePlannerTable';
import { IssuePlannerToolbar } from './IssuePlannerToolbar';
import type {
  IssuePlannerCartItem,
  IssuePlannerItem,
  IssuePlannerSizeOption,
  IssuePlannerView,
} from './types';
import { NON_SIZED_OPTION_ID } from './types';
import { useIeDbCartSync } from './useDbCartSync';
import { useAssetAllocator, useReservedAssets } from './useAssetLogic';
import { extractErrorMessage } from '../workflowUtils';
import { iemService, type IEItem } from '@/services/iem';
import type { Entity } from '@/types/Entity';
import {
  useIESummary,
  useCatalogChildren,
  useSizingForCustomer,
} from '@/hooks/queries/iem/useIEMQueries';
import { useEntities } from '@/hooks/queries/entities/useEntities';
import { useHierarchyEntities } from '@/hooks/queries/entities/useHierarchyEntities';
import { useNotifications, useNotificationStore } from '@/stores/useNotificationStore';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useUpsertSizeAllocationCart } from '@/hooks/mutations/cart/useCartMutations';
import { cartService } from '@/services/cart';
import { buildReserveStockPlan } from '@/components/iem/shared/reserveStockPlanner';
import { EXCLUDE_PNP_CATALOG_TYPE_KEEP_NULL } from '@/utils/iemFilters';
import { httpClient } from '@/utils/httpClient';
import { logger } from '@equip/utils/logger';

const log = logger.child({ component: 'IssueIeTab' });

export interface IssueIeTabOptions {
  open: boolean;
  active?: boolean;
  customerId?: string;
  externalReservedAssetIds: string[];
  onReservedAssetIdsChange?: (ids: string[]) => void;
  includeOwnReservedInAvailability?: boolean;
  preferBestQuantityLocationDefault?: boolean;
  readOnly?: boolean;
}

export interface IssueIeTabActions {
  addAllAvailable: () => Promise<void>;
  adjustQuantity: (itemId: string, delta: number) => Promise<void>;
  removeItem: (itemId: string) => void;
  clearCart: () => void;
}

export interface IssueIeTabResult {
  toolbar: ReactNode | null;
  content: ReactNode;
  cartItems: IssuePlannerCartItem[];
  plannerItems: IssuePlannerItem[];
  hasAddableItems: boolean;
  plannerActionId: string | null;
  isBulkProcessing: boolean;
  actions: IssueIeTabActions;
  reservedAssetIds: string[];
}

export const shouldShowIssueSizeLoadingSkeleton = ({
  hasSizedItems,
  isCatalogChildrenLoading,
  isSizeAvailabilityLoading: _isSizeAvailabilityLoading,
}: {
  hasSizedItems: boolean;
  isCatalogChildrenLoading: boolean;
  isSizeAvailabilityLoading: boolean;
}): boolean => {
  // Size choices depend on the catalog-children query. Availability counts are
  // useful, but they should load in the background instead of blocking the UI.
  return hasSizedItems && isCatalogChildrenLoading;
};

const sanitizePlannerKeyPart = (value?: string | null): string | null => {
  if (value == null) {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed || trimmed === '—') {
    return null;
  }
  return trimmed;
};

const buildPlannerItemId = (ieItem: IEItem): string => {
  const normalizedCatalogId = sanitizePlannerKeyPart(ieItem.catalogId);
  const normalizedIeKitId = sanitizePlannerKeyPart(ieItem.ieKitId);

  if (normalizedCatalogId) {
    // If we have a kit ID, combine it with catalog ID to distinguish the same item across different kits
    if (normalizedIeKitId) {
      return `${normalizedCatalogId}-${normalizedIeKitId}`;
    }
    return normalizedCatalogId;
  }

  const normalizedKitItemId = sanitizePlannerKeyPart(ieItem.kitItemId);
  if (normalizedKitItemId) {
    return normalizedKitItemId;
  }

  const fallbackParts = [
    sanitizePlannerKeyPart(ieItem.catalogId),
    sanitizePlannerKeyPart(ieItem.personIeKitId),
    sanitizePlannerKeyPart(ieItem.kitAssignmentName),
    sanitizePlannerKeyPart(ieItem.nsn),
    sanitizePlannerKeyPart(ieItem.lin),
    sanitizePlannerKeyPart(ieItem.item),
  ].filter((part): part is string => Boolean(part));

  if (fallbackParts.length > 0) {
    return fallbackParts.join('|');
  }

  return `${ieItem.nsn}-${ieItem.lin}`;
};

const resolveKitDisplayName = (ieItem: IEItem): string => {
  const kitName = typeof ieItem.kitName === 'string' ? ieItem.kitName.trim() : '';
  if (kitName) {
    return kitName;
  }
  const assignmentName =
    typeof ieItem.kitAssignmentName === 'string' ? ieItem.kitAssignmentName.trim() : '';
  return assignmentName || '—';
};

export const useIssueIeTab = ({
  open,
  active = true,
  customerId,
  externalReservedAssetIds,
  onReservedAssetIdsChange,
  includeOwnReservedInAvailability = false,
  preferBestQuantityLocationDefault = false,
  readOnly = false,
}: IssueIeTabOptions): IssueIeTabResult => {
  const isTabActive = open && active;
  const notifications = useNotifications();
  const { label: ieKitLabel } = useIeKitLabels('front_window');
  const { primaryLabel, secondaryLabel, relatedLabel } = useLocationCategoryLabels();
  const activeNotifications = useNotificationStore((state) => state.notifications);
  const { data: ieData = [], isLoading } = useIESummary(
    customerId || '',
    EXCLUDE_PNP_CATALOG_TYPE_KEEP_NULL,
    isTabActive,
  );
  const { data: sizingData } = useSizingForCustomer(customerId || '', isTabActive);
  const resolvedSizingData = useMemo(() => {
    return Array.isArray(sizingData) ? sizingData : [];
  }, [sizingData]);

  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refreshAvailability = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);
  const sizingByParentCatalogId = useMemo(() => {
    const map = new Map<string, string>();
    resolvedSizingData.forEach((entry) => {
      const parentId = entry.parentCatalogId ?? entry.catalogId;
      if (!parentId || !entry.catalogId) {
        return;
      }
      map.set(parentId, entry.catalogId);
    });
    return map;
  }, [resolvedSizingData]);

  // Extract IE Kit catalog IDs (parent catalogs from IE Kit data)
  const ieParentCatalogIds = useMemo(() => {
    const ids = ieData
      .map((entry) => (typeof entry.catalogId === 'string' ? entry.catalogId.trim() : ''))
      .filter((id): id is string => Boolean(id && id.length > 0));
    return Array.from(new Set(ids));
  }, [ieData]);

  // Fetch catalog children for sizing options
  const { data: catalogChildren = [], isLoading: isCatalogChildrenLoading } = useCatalogChildren({
    parentCatalogIds: ieParentCatalogIds,
    enabled: isTabActive && ieParentCatalogIds.length > 0,
  });

  // Fetch parent catalog entities to resolve their primary (storefront) location
  const catalogIdFilter = useMemo(() => {
    if (ieParentCatalogIds.length === 0) return undefined;
    return ieParentCatalogIds.length === 1
      ? `eq.${ieParentCatalogIds[0]}`
      : `in.(${ieParentCatalogIds.join(',')})`;
  }, [ieParentCatalogIds]);

  const { data: parentCatalogEntities = [] } = useEntities({
    type: 'catalog',
    filters: catalogIdFilter ? { id: catalogIdFilter } : undefined,
    limit: ieParentCatalogIds.length || 100,
    enabled: isTabActive && ieParentCatalogIds.length > 0,
  });

  // Fetch child catalog entities (size variants) in batches to avoid URL length limits
  const childCatalogIds = useMemo(() => catalogChildren.map((c) => c.id), [catalogChildren]);

  const { data: childCatalogEntities = [], isLoading: isChildEntitiesLoading } = useQuery({
    queryKey: ['catalog_entities_for_locations', 'issue_ie_children', childCatalogIds],
    queryFn: async () => {
      if (childCatalogIds.length === 0) return [];
      const BATCH_SIZE = 80;
      const batches: string[][] = [];
      for (let i = 0; i < childCatalogIds.length; i += BATCH_SIZE) {
        batches.push(childCatalogIds.slice(i, i + BATCH_SIZE));
      }
      const results = await Promise.all(
        batches.map((batch) => {
          const filter = batch.length === 1 ? `eq.${batch[0]}` : `in.(${batch.join(',')})`;
          return httpClient.getAll<Entity>(
            `/api/v1/entities?entity_type=eq.catalog&is_current=eq.true&id=${filter}&select=id,entity_data`,
          );
        }),
      );
      return results.flat();
    },
    staleTime: 60_000,
    enabled: isTabActive && childCatalogIds.length > 0,
  });

  // Build map of catalogId → primary location (primary_location_id)
  // Includes both parent catalog IDs and their children (with their own locations)
  const locationIdByCatalogId = useMemo(() => {
    const map = new Map<string, string>();
    const allEntities = [...parentCatalogEntities, ...childCatalogEntities];
    allEntities.forEach((entity) => {
      const data = (entity.entity_data ?? {}) as Record<string, unknown>;
      const storefrontLocId =
        typeof data.primary_location_id === 'string'
          ? data.primary_location_id.trim()
          : null;
      const warehouseLocId =
        typeof data.secondary_location_id === 'string'
          ? data.secondary_location_id.trim()
          : null;
      // Prefer storefront (issue-from location), fall back to warehouse
      const locationId = storefrontLocId || warehouseLocId;
      if (locationId && entity.id) {
        map.set(entity.id, locationId);
      }
    });
    // Inherit parent location for children that don't have their own
    catalogChildren.forEach((child) => {
      const parentId = child.parentCatalogId?.trim();
      const childId = child.id?.trim();
      if (parentId && childId && map.has(parentId) && !map.has(childId)) {
        map.set(childId, map.get(parentId)!);
      }
    });
    return map;
  }, [parentCatalogEntities, childCatalogEntities, catalogChildren]);

  // Build primary/secondary location ID maps for catalog items (parents + children)
  const primaryLocationByCatalogId = useMemo(() => {
    const map = new Map<string, string>();
    const allEntities = [...parentCatalogEntities, ...childCatalogEntities];
    allEntities.forEach((entity) => {
      const data = (entity.entity_data ?? {}) as Record<string, unknown>;
      const locId =
        typeof data.primary_location_id === 'string' ? data.primary_location_id.trim() : null;
      if (locId && entity.id) map.set(entity.id, locId);
    });
    return map;
  }, [parentCatalogEntities, childCatalogEntities]);

  const secondaryLocationByCatalogId = useMemo(() => {
    const map = new Map<string, string>();
    const allEntities = [...parentCatalogEntities, ...childCatalogEntities];
    allEntities.forEach((entity) => {
      const data = (entity.entity_data ?? {}) as Record<string, unknown>;
      const locId =
        typeof data.secondary_location_id === 'string' ? data.secondary_location_id.trim() : null;
      if (locId && entity.id) map.set(entity.id, locId);
    });
    return map;
  }, [parentCatalogEntities, childCatalogEntities]);

  // Combined parent + child catalog IDs for barcode_mappings lookup
  const allCatalogIds = useMemo(() => {
    const ids = new Set(ieParentCatalogIds);
    catalogChildren.forEach((child) => {
      if (child.id) ids.add(child.id);
    });
    return Array.from(ids);
  }, [ieParentCatalogIds, catalogChildren]);

  // Fetch barcode_mappings to find related locations for the catalog items (batched)
  const { data: barcodeMappings = [] } = useQuery({
    queryKey: ['barcode_mappings', 'issue_ie', allCatalogIds],
    queryFn: async () => {
      if (allCatalogIds.length === 0) return [];
      const BATCH_SIZE = 80;
      const batches: string[][] = [];
      for (let i = 0; i < allCatalogIds.length; i += BATCH_SIZE) {
        batches.push(allCatalogIds.slice(i, i + BATCH_SIZE));
      }
      const results = await Promise.all(
        batches.map((batch) => {
          const filter = batch.length === 1 ? `eq.${batch[0]}` : `in.(${batch.join(',')})`;
          return httpClient.getAll<{ catalog_id: string; location_id: string }>(
            `/api/v1/barcode_mappings?catalog_id=${filter}&select=catalog_id,location_id`,
          );
        }),
      );
      return results.flat();
    },
    staleTime: 60_000,
    enabled: isTabActive && allCatalogIds.length > 0,
  });

  // Build related location IDs by catalog (from barcode_mappings)
  const relatedLocationsByCatalogId = useMemo(() => {
    const map = new Map<string, string[]>();
    barcodeMappings.forEach(({ catalog_id, location_id }) => {
      const existing = map.get(catalog_id) ?? [];
      if (!existing.includes(location_id)) {
        existing.push(location_id);
      }
      map.set(catalog_id, existing);
    });
    return map;
  }, [barcodeMappings]);

  // Fetch location hierarchy for name resolution
  const locationHierarchyQuery = useHierarchyEntities({
    dataSource: {
      endpoint: '/api/v1/location_hierarchy',
      order: 'breadcrumb.asc',
    },
    filters: {
      select: 'id,display_name,entity_identifier,breadcrumb,breadcrumb_names',
    },
    enabled: isTabActive,
  });

  const locationNameById = useMemo(() => {
    const map = new Map<string, string>();
    (locationHierarchyQuery.data ?? []).forEach((loc) => {
      if (loc.id && loc.display_name) map.set(loc.id, loc.display_name);
    });
    return map;
  }, [locationHierarchyQuery.data]);

  const locationDropdownLabelById = useMemo(() => {
    const map = new Map<string, string>();
    (locationHierarchyQuery.data ?? []).forEach((loc) => {
      if (!loc.id) {
        return;
      }

      const breadcrumbNames = Array.isArray(loc.breadcrumb_names)
        ? loc.breadcrumb_names.filter(
            (value): value is string => typeof value === 'string' && value.trim().length > 0,
          )
        : [];

      // Location templates can define up to level-2 labels (e.g. Row, Shelf).
      // Show level 1, or level 1 + level 2 when both exist.
      const templateLevels = breadcrumbNames.slice(1, 3);
      const label = templateLevels.join(' / ').trim();
      map.set(loc.id, label || loc.display_name || loc.id);
    });
    return map;
  }, [locationHierarchyQuery.data]);

  // Per-item selected location state (defaults to primary location)
  const [selectedLocationByItemId, setSelectedLocationByItemId] = useState<Record<string, string | undefined>>({});

  const handleLocationChange = useCallback((itemId: string, locationId: string) => {
    setSelectedLocationByItemId((prev) => ({ ...prev, [itemId]: locationId }));
  }, []);

  // Catalog IDs for location availability. Keep both parent and child IDs so
  // parent rows can surface their full location counts in the selector.
  const locationCatalogIds = useMemo(() => {
    const ids = new Set<string>();
    ieParentCatalogIds.forEach((id) => ids.add(id));
    catalogChildren.forEach((child) => {
      if (child.id) ids.add(child.id);
    });
    return Array.from(ids);
  }, [ieParentCatalogIds, catalogChildren]);

  // Fetch available asset counts per (catalog_id, location_id)
  const { data: locationAvailabilityData = { located: new Map(), unlocated: new Map() } } = useQuery({
    queryKey: ['location_availability', 'issue_ie', locationCatalogIds],
    queryFn: async () => {
      if (locationCatalogIds.length === 0) return { located: new Map<string, Map<string, number>>(), unlocated: new Map<string, number>() };
      const counts: Record<string, Record<string, number>> = await iemService.getAvailableAssetCountsByCatalogIds({
        catalogIds: locationCatalogIds,
      });
      // Convert { catalogId: { locationId: count } } to Map<string, Map<string, number>>
      const located = new Map<string, Map<string, number>>();
      const unlocated = new Map<string, number>();
      for (const [catalogId, locationCounts] of Object.entries(counts)) {
        const locMap = new Map<string, number>();
        for (const [locationId, count] of Object.entries(locationCounts as Record<string, number>)) {
          if (locationId === '_total') {
            if (count > 0) unlocated.set(catalogId, count);
          } else {
            locMap.set(locationId, count);
          }
        }
        if (locMap.size > 0) located.set(catalogId, locMap);
      }
      return { located, unlocated };
    },
    staleTime: 30_000,
    enabled: isTabActive && locationCatalogIds.length > 0,
  });

  const locationAvailability = locationAvailabilityData.located;
  const unlocatedCountByCatalogId = locationAvailabilityData.unlocated;

  // Fetch on-hand asset counts per (catalog_id, location_id) — includes reserved assets.
  // Used only for location label display (e.g. "UBB006 (100)") so users see the physical
  // stock at each location, not the reduced unreserved count.
  const { data: locationOnhandData = { located: new Map(), unlocated: new Map() } } = useQuery({
    queryKey: ['location_onhand', 'issue_ie', locationCatalogIds],
    queryFn: async () => {
      if (locationCatalogIds.length === 0) return { located: new Map<string, Map<string, number>>(), unlocated: new Map<string, number>() };
      const counts: Record<string, Record<string, number>> = await iemService.getOnhandAssetCountsByCatalogIds({
        catalogIds: locationCatalogIds,
      });
      const located = new Map<string, Map<string, number>>();
      const unlocated = new Map<string, number>();
      for (const [catalogId, locationCounts] of Object.entries(counts)) {
        const locMap = new Map<string, number>();
        for (const [locationId, count] of Object.entries(locationCounts as Record<string, number>)) {
          if (locationId === '_total') {
            if (count > 0) unlocated.set(catalogId, count);
          } else {
            locMap.set(locationId, count);
          }
        }
        if (locMap.size > 0) located.set(catalogId, locMap);
      }
      return { located, unlocated };
    },
    staleTime: 30_000,
    enabled: isTabActive && locationCatalogIds.length > 0,
  });

  const locationOnhand = locationOnhandData.located;
  const unlocatedOnhandByCatalogId = locationOnhandData.unlocated;

  // Build the grouped location options per catalog item (parents + children)
  // Order: Primary (default) → Secondary → Related (from barcode_mappings, excluding primary/secondary)
  const locationOptionsByItemId = useMemo(() => {
    const map = new Map<string, LocationOption[]>();

    const buildOptions = (catalogId: string, parentCatalogId?: string) => {
      const options: LocationOption[] = [];
      // Children inherit primary/secondary from parent
      const primaryId = primaryLocationByCatalogId.get(catalogId) ?? (parentCatalogId ? primaryLocationByCatalogId.get(parentCatalogId) : undefined);
      const secondaryId = secondaryLocationByCatalogId.get(catalogId) ?? (parentCatalogId ? secondaryLocationByCatalogId.get(parentCatalogId) : undefined);
      const usedIds = new Set<string>();
      // locCounts: unreserved — used only to discover stocked locations not in primary/secondary/related
      const locCounts = locationAvailability.get(catalogId);
      // onhandCounts: includes reserved — used for the label quantity shown to the user
      const onhandCounts = locationOnhand.get(catalogId);

      if (primaryId) {
        const name = locationDropdownLabelById.get(primaryId) ?? primaryId;
        options.push({
          id: primaryId, label: name, group: primaryLabel,
          qty: onhandCounts?.get(primaryId) ?? locCounts?.get(primaryId) ?? 0,
          availableQty: locCounts?.get(primaryId) ?? 0,
        });
        usedIds.add(primaryId);
      }

      if (secondaryId && !usedIds.has(secondaryId)) {
        const name = locationDropdownLabelById.get(secondaryId) ?? secondaryId;
        options.push({
          id: secondaryId, label: name, group: secondaryLabel,
          qty: onhandCounts?.get(secondaryId) ?? locCounts?.get(secondaryId) ?? 0,
          availableQty: locCounts?.get(secondaryId) ?? 0,
        });
        usedIds.add(secondaryId);
      }

      const related = relatedLocationsByCatalogId.get(catalogId) ?? [];
      related.forEach((locId) => {
        if (!usedIds.has(locId)) {
          const name = locationDropdownLabelById.get(locId) ?? locId;
          options.push({
            id: locId, label: name, group: relatedLabel,
            qty: onhandCounts?.get(locId) ?? locCounts?.get(locId) ?? 0,
            availableQty: locCounts?.get(locId) ?? 0,
          });
          usedIds.add(locId);
        }
      });

      // Add any locations with available assets that aren't already primary/secondary/related
      if (locCounts) {
        locCounts.forEach((count: number, locId: string) => {
          if (!usedIds.has(locId) && count > 0) {
            const name = locationDropdownLabelById.get(locId) ?? locId;
            options.push({
              id: locId, label: name, group: relatedLabel,
              qty: onhandCounts?.get(locId) ?? count,
              availableQty: count,
            });
            usedIds.add(locId);
          }
        });
      }

      // Show unlocated assets as a disabled "Unknown Location" entry so users
      // understand why size availability may differ from location-level counts
      const unlocatedCount = unlocatedOnhandByCatalogId.get(catalogId) ?? unlocatedCountByCatalogId.get(catalogId) ?? 0;
      if (unlocatedCount > 0) {
        options.push({ id: '_unlocated', label: 'Unknown Location', qty: unlocatedCount, disabled: true });
      }

      map.set(catalogId, options);
    };

    // Build for parent catalog IDs
    ieParentCatalogIds.forEach((catalogId) => buildOptions(catalogId));

    // Build for child catalog IDs (size variants) — inherit primary/secondary from parent
    catalogChildren.forEach((child) => {
      if (child.id && !map.has(child.id)) {
        buildOptions(child.id, child.parentCatalogId);
      }
    });

    return map;
  }, [ieParentCatalogIds, catalogChildren, primaryLocationByCatalogId, secondaryLocationByCatalogId, relatedLocationsByCatalogId, locationDropdownLabelById, locationAvailability, locationOnhand, unlocatedCountByCatalogId, unlocatedOnhandByCatalogId, primaryLabel, secondaryLabel, relatedLabel]);

  // Map planner item IDs to their catalog's location options
  // (will be populated later once basePlannerItems is available, but define a helper now)
  const getLocationOptionsForCatalogId = useCallback((catalogId: string | null | undefined): LocationOption[] => {
    if (!catalogId) return [];
    return locationOptionsByItemId.get(catalogId) ?? [];
  }, [locationOptionsByItemId]);

  // Resolve effective location for an item using explicit user selection first,
  // then defaulting to the catalog primary location.
  const resolveLocationForItem = useCallback((
    plannerItemId: string,
    parentCatalogId: string | null | undefined,
    effectiveCatalogId?: string | null,
  ): string | undefined => {
    const selectedId = selectedLocationByItemId[plannerItemId];
    if (selectedId) return selectedId;

    const parentId = parentCatalogId?.trim() ?? null;
    const effectiveId = effectiveCatalogId?.trim() || parentId;
    if (!effectiveId) return undefined;

    const defaultLocId =
      primaryLocationByCatalogId.get(effectiveId) ??
      (parentId ? primaryLocationByCatalogId.get(parentId) : undefined) ??
      locationIdByCatalogId.get(effectiveId) ??
      (parentId ? locationIdByCatalogId.get(parentId) : undefined);

    const options =
      locationOptionsByItemId.get(effectiveId) ??
      (parentId ? locationOptionsByItemId.get(parentId) : undefined);

    if (preferBestQuantityLocationDefault && options && options.length > 0) {
      const selectableOptions = options.filter((opt) => !opt.disabled);
      if (selectableOptions.length > 0) {
        let best = selectableOptions[0]!;
        let bestQty = best.availableQty ?? best.qty ?? 0;
        for (const option of selectableOptions.slice(1)) {
          const qty = option.availableQty ?? option.qty ?? 0;
          if (qty > bestQty) {
            best = option;
            bestQty = qty;
          }
        }
        return best.id;
      }
    }

    if (options && options.length > 0) {
      // Keep primary/default location selected even when out of stock.
      const firstSelectable = options.find((opt) => !opt.disabled);
      if (!defaultLocId && firstSelectable) return firstSelectable.id;
    }

    return defaultLocId;
  }, [selectedLocationByItemId, primaryLocationByCatalogId, locationIdByCatalogId, locationOptionsByItemId, preferBestQuantityLocationDefault]);

  // Database cart sync - filters by item_source='ie' automatically
  const dbCart = useIeDbCartSync(customerId, open);
  const ieCartItems = dbCart.cartItems;
  const ieKitNameById = useMemo(() => {
    const map = new Map<string, string>();
    ieData.forEach((entry) => {
      if (entry.ieKitId && entry.kitName) {
        map.set(entry.ieKitId, entry.kitName);
      }
    });
    return map;
  }, [ieData]);

  // Build a map from catalogId → projected expiration date (today + duration days)
  const expirationDateByCatalogId = useMemo(() => {
    const map = new Map<string, string>();
    const today = new Date();
    ieData.forEach((entry) => {
      const catalogId = entry.catalogId?.trim();
      const days = entry.expirationDurationDays;
      if (!catalogId || !days || days <= 0) return;
      if (map.has(catalogId)) return;
      const expDate = new Date(today);
      expDate.setDate(expDate.getDate() + days);
      map.set(catalogId, expDate.toISOString().slice(0, 10));
    });
    return map;
  }, [ieData]);

  // Build child→parent catalog ID map early so expiration lookup can resolve sized items
  const parentCatalogIdByChildCatalogIdEarly = useMemo(() => {
    const map = new Map<string, string>();
    catalogChildren.forEach((child) => {
      const childCatalogId = child.id?.trim();
      const parentCatalogId = child.parentCatalogId?.trim();
      if (!childCatalogId || !parentCatalogId) return;
      map.set(childCatalogId, parentCatalogId);
    });
    return map;
  }, [catalogChildren]);

  const ieCartItemsWithKitName = useMemo(() => {
    return ieCartItems.map((item) => {
      const selectedId = item.selectedCatalogId?.trim() ?? null;
      // For sized items, selectedCatalogId is the child catalog. Resolve to parent for expiration lookup.
      const parentId = selectedId
        ? (parentCatalogIdByChildCatalogIdEarly.get(selectedId) ?? selectedId)
        : null;
      const expirationDate =
        (parentId && expirationDateByCatalogId.get(parentId)) ||
        (selectedId && expirationDateByCatalogId.get(selectedId)) ||
        (item.plannerItemId && expirationDateByCatalogId.get(item.plannerItemId)) ||
        null;

      const sourceLocationLabel = (() => {
        const locationIds = item.sourceLocationIds ?? [];
        if (locationIds.length === 0) return null;
        if (locationIds.length === 1) {
          const locationId = locationIds[0];
          if (!locationId) return null;
          return locationNameById.get(locationId) ?? locationId;
        }
        return 'Multiple locations';
      })();

      return {
        ...item,
        kitName: item.ieKitId ? (ieKitNameById.get(item.ieKitId) ?? null) : null,
        sourceLocationLabel,
        expirationDate: expirationDate ?? null,
      };
    });
  }, [
    ieCartItems,
    ieKitNameById,
    locationNameById,
    expirationDateByCatalogId,
    parentCatalogIdByChildCatalogIdEarly,
  ]);
  const plannedByCatalogId = useMemo(() => {
    const map = new Map<string, number>();
    (dbCart.placeholders ?? []).forEach((placeholder) => {
      const parentId = placeholder.parent_catalog_id?.trim();
      if (!parentId) {
        return;
      }
      const qty =
        typeof placeholder.quantity === 'number'
          ? placeholder.quantity
          : Number(placeholder.quantity) || 0;
      map.set(parentId, (map.get(parentId) ?? 0) + Math.max(0, qty));
    });
    return map;
  }, [dbCart.placeholders]);

  const { createReservedAssetSet, reservedAssetIds } = useReservedAssets({
    externalReservedAssetIds,
    cartReservedAssetIds: dbCart.reservedAssetIds,
  });

  const { allocateAssets } = useAssetAllocator({
    dbCart,
    createReservedAssetSet,
    refreshAvailability,
  });

  const [view, setView] = useState<IssuePlannerView>('missing');
  const [plannerActionId, setPlannerActionId] = useState<string | null>(null);
  const [isBulkAdding, setIsBulkAdding] = useState(false);
  const [sizeSelections, setSizeSelections] = useState<Record<string, string | undefined>>({});
  const [quantitySelections, setQuantitySelections] = useState<Record<string, number | undefined>>(
    {},
  );
  const [sizeAvailabilityBaseline, setSizeAvailabilityBaseline] = useState<Record<string, number>>(
    {},
  );
  const [isSizeAvailabilityLoading, setIsSizeAvailabilityLoading] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [selectedKit, setSelectedKit] = useState<string>('all');
  const upsertSizeAllocationMutation = useUpsertSizeAllocationCart();

  useEffect(() => {
    if (!open) {
      // Don't clear cart on close - it persists in the database
      setView('missing');
      setPlannerActionId(null);
      setIsBulkAdding(false);
      setSearch('');
      setSizeSelections({});
      setQuantitySelections({});
      setSizeAvailabilityBaseline({});
      setIsSizeAvailabilityLoading(false);
      setSelectedKit('all');
      setSelectedLocationByItemId({});
    }
  }, [open]);

  const plannerData = useMemo(() => {
    const groups = new Map<
      string,
      {
        id: string;
        item: string;
        lin: string;
        nsn: string;
        authorizedQty: number;
        currentQty: number;
        availableQty: number;
        catalogId: string | null;
        ieKitId: string | null;
        kitNames: Set<string>;
      }
    >();

    ieData
      .filter((ieItem) => (ieItem.catalogType || '').toLowerCase() !== 'buy program')
      .forEach((ieItem) => {
        const id = buildPlannerItemId(ieItem);
        const kitDisplayName = resolveKitDisplayName(ieItem);
        const kitName = kitDisplayName && kitDisplayName !== '—' ? kitDisplayName : null;
        const authorizedQty = ieItem.authorizedQty ?? 0;
        const currentQty = ieItem.currentQty ?? 0;
        const availableQty = ieItem.availableQty ?? 0;
        const ieKitId = ieItem.ieKitId ?? null;

        const existing = groups.get(id);
        if (existing) {
          existing.authorizedQty += authorizedQty;
          existing.currentQty = Math.max(existing.currentQty, currentQty);
          existing.availableQty = Math.max(existing.availableQty, availableQty);
          if (kitName) {
            existing.kitNames.add(kitName);
          }
          if ((existing.lin === '—' || !existing.lin) && ieItem.lin) {
            existing.lin = ieItem.lin;
          }
          if ((existing.nsn === '—' || !existing.nsn) && ieItem.nsn) {
            existing.nsn = ieItem.nsn;
          }
          if ((!existing.item || existing.item === '—') && ieItem.item) {
            existing.item = ieItem.item;
          }
          if (!existing.catalogId && ieItem.catalogId) {
            existing.catalogId = ieItem.catalogId;
          }
          if (!existing.ieKitId && ieKitId) {
            existing.ieKitId = ieKitId;
          }
          return;
        }

        groups.set(id, {
          id,
          item: ieItem.item,
          lin: ieItem.lin || '—',
          nsn: ieItem.nsn,
          authorizedQty,
          currentQty,
          availableQty,
          catalogId: ieItem.catalogId ?? null,
          ieKitId,
          kitNames: kitName ? new Set([kitName]) : new Set(),
        });
      });

    const kitNamesByItemId = new Map<string, Set<string>>();
    const basePlannerItems = Array.from(groups.values()).map((entry) => {
      const kitNames = Array.from(entry.kitNames).sort();
      kitNamesByItemId.set(entry.id, new Set(kitNames));

      return {
        id: entry.id,
        item: entry.item,
        lin: entry.lin || '—',
        nsn: entry.nsn,
        need: entry.authorizedQty - entry.currentQty,
        authorizedQty: entry.authorizedQty,
        currentQty: entry.currentQty,
        cartName: entry.item,
        catalogId: entry.catalogId ?? null,
        ieKitId: entry.ieKitId,
        kitName: kitNames.length > 0 ? kitNames.join(', ') : '—',
        availableQty: entry.availableQty,
      };
    });

    const uniqueKitNames = Array.from(kitNamesByItemId.values()).flatMap((names) =>
      Array.from(names),
    );

    return {
      basePlannerItems,
      kitNamesByItemId,
      uniqueKitNames: Array.from(new Set(uniqueKitNames)).sort(),
    };
  }, [ieData]);

  const { basePlannerItems, kitNamesByItemId, uniqueKitNames } = plannerData;

  const plannerItems = useMemo<IssuePlannerItem[]>(() => {
    return basePlannerItems.map((item) => ({
      ...item,
      quantityToIssue: quantitySelections[item.id],
    }));
  }, [basePlannerItems, quantitySelections]);

  const catalogIdByPlannerId = useMemo(() => {
    const map = new Map<string, string>();
    basePlannerItems.forEach((item) => {
      const catalogId = item.catalogId?.trim();
      if (catalogId) {
        map.set(item.id, catalogId);
      }
    });
    return map;
  }, [basePlannerItems]);

  const primaryPlannerIdByCatalogId = useMemo(() => {
    const normalizeKitLabel = (value?: string | null) => {
      const trimmed = (value ?? '').trim();
      if (!trimmed || trimmed === '—') {
        return '~~~';
      }
      return trimmed;
    };

    const sorted = [...basePlannerItems].sort((a, b) => {
      const catalogCompare = (a.catalogId ?? '').localeCompare(b.catalogId ?? '');
      if (catalogCompare !== 0) {
        return catalogCompare;
      }
      const kitCompare = normalizeKitLabel(a.kitName).localeCompare(normalizeKitLabel(b.kitName));
      if (kitCompare !== 0) {
        return kitCompare;
      }
      return a.id.localeCompare(b.id);
    });

    const map = new Map<string, string>();
    sorted.forEach((item) => {
      const catalogId = item.catalogId?.trim();
      if (!catalogId || map.has(catalogId)) {
        return;
      }
      map.set(catalogId, item.id);
    });
    return map;
  }, [basePlannerItems]);

  const plannerIdByParentCatalogAndKit = useMemo(() => {
    const map = new Map<string, string>();
    basePlannerItems.forEach((item) => {
      const parentCatalogId = item.catalogId?.trim();
      const ieKitId = item.ieKitId?.trim();
      if (!parentCatalogId || !ieKitId) {
        return;
      }
      map.set(`${parentCatalogId}::${ieKitId}`, item.id);
    });
    return map;
  }, [basePlannerItems]);

  // Alias to the early-computed map (already built before ieCartItemsWithKitName)
  const parentCatalogIdByChildCatalogId = parentCatalogIdByChildCatalogIdEarly;

  const resolvePlannerItemIdForCartItem = useCallback(
    (item: IssuePlannerCartItem): string => {
      const selectedCatalogId = item.selectedCatalogId?.trim();
      const parentCatalogId = selectedCatalogId
        ? (parentCatalogIdByChildCatalogId.get(selectedCatalogId) ?? selectedCatalogId)
        : null;

      if (parentCatalogId && item.ieKitId) {
        const ieKitId = item.ieKitId.trim();
        if (ieKitId) {
          const plannerId = plannerIdByParentCatalogAndKit.get(`${parentCatalogId}::${ieKitId}`);
          if (plannerId) {
            return plannerId;
          }
        }
      }

      if (parentCatalogId) {
        const primaryPlannerId = primaryPlannerIdByCatalogId.get(parentCatalogId);
        if (primaryPlannerId) {
          return primaryPlannerId;
        }
      }

      return item.plannerItemId ?? item.id;
    },
    [parentCatalogIdByChildCatalogId, plannerIdByParentCatalogAndKit, primaryPlannerIdByCatalogId],
  );

  const ieCartMap = useMemo(() => {
    const map = new Map<string, IssuePlannerCartItem>();

    ieCartItemsWithKitName.forEach((item) => {
      const plannerId = resolvePlannerItemIdForCartItem(item);
      const selectedCatalogId = item.selectedCatalogId?.trim() ?? null;
      const parentCatalogId = selectedCatalogId
        ? (parentCatalogIdByChildCatalogId.get(selectedCatalogId) ?? selectedCatalogId)
        : null;

      const fallbackLocationId = resolveLocationForItem(
        plannerId,
        parentCatalogId,
        selectedCatalogId ?? parentCatalogId,
      );
      const fallbackLocationLabel = fallbackLocationId
        ? (locationNameById.get(fallbackLocationId) ?? fallbackLocationId)
        : null;

      const existing = map.get(plannerId);
      if (existing) {
        const mergedLocationIds = Array.from(
          new Set([...(existing.sourceLocationIds ?? []), ...(item.sourceLocationIds ?? [])]),
        );

        let mergedLocationLabel: string | null = null;
        if (mergedLocationIds.length === 1) {
          const locationId = mergedLocationIds[0];
          mergedLocationLabel = locationId ? (locationNameById.get(locationId) ?? locationId) : null;
        } else if (mergedLocationIds.length > 1) {
          mergedLocationLabel = 'Multiple locations';
        }

        if (!mergedLocationLabel) {
          mergedLocationLabel = fallbackLocationLabel;
        }

        // Aggregate assets for the table view
        map.set(plannerId, {
          ...existing,
          assetIds: [...existing.assetIds, ...item.assetIds],
          sourceLocationIds: mergedLocationIds,
          sourceLocationLabel: mergedLocationLabel,
        });
      } else {
        map.set(plannerId, {
          ...item,
          plannerItemId: plannerId,
          sourceLocationLabel: item.sourceLocationLabel ?? fallbackLocationLabel,
        });
      }
    });

    return map;
  }, [
    ieCartItemsWithKitName,
    locationNameById,
    parentCatalogIdByChildCatalogId,
    resolveLocationForItem,
    resolvePlannerItemIdForCartItem,
  ]);

  const plannedRequestCounts = useMemo(() => {
    const result: Record<string, number> = {};
    basePlannerItems.forEach((item) => {
      const parentId = item.catalogId?.trim();
      if (!parentId) return;
      result[item.id] = plannedByCatalogId.get(parentId) ?? 0;
    });
    return result;
  }, [basePlannerItems, plannedByCatalogId]);

  const sizeOptionsByParentId = useMemo(() => {
    const map = new Map<string, IssuePlannerSizeOption[]>();
    catalogChildren.forEach((child) => {
      const parentId = child.parentCatalogId?.trim();
      if (!parentId) {
        return;
      }
      const options = map.get(parentId) ?? [];
      options.push({
        catalogId: child.id,
        displayName: child.displayName,
        sizeLabel: child.sizeLabel ?? null,
        entityIdentifier: child.entityIdentifier ?? null,
        nsn: child.nsn ?? null,
      });
      map.set(parentId, options);
    });

    // Add a synthetic "non-sized" option per parent so users can explicitly
    // choose to defer size selection (records a request line instead of a
    // concrete reservation).
    map.forEach((options, parentId) => {
      options.push({
        catalogId: NON_SIZED_OPTION_ID,
        displayName: 'Non-sized (pick later)',
        sizeLabel: null,
        entityIdentifier: null,
        nsn: null,
      });
      map.set(parentId, options);
    });

    map.forEach((options) => {
      options.sort((a, b) => {
        if (a.catalogId === NON_SIZED_OPTION_ID) return -1;
        if (b.catalogId === NON_SIZED_OPTION_ID) return 1;
        return a.displayName.localeCompare(b.displayName);
      });
    });

    return map;
  }, [catalogChildren]);

  const sizeOptionsByItemId = useMemo(() => {
    const map = new Map<string, IssuePlannerSizeOption[]>();
    basePlannerItems.forEach((item) => {
      const parentId = item.catalogId?.trim();
      if (!parentId) {
        return;
      }
      const options = sizeOptionsByParentId.get(parentId);
      if (!options || options.length === 0) {
        return;
      }
      map.set(item.id, options);
    });
    return map;
  }, [basePlannerItems, sizeOptionsByParentId]);

  const sizeParentByCatalogId = useMemo(() => {
    const map = new Map<string, string>();
    sizeOptionsByItemId.forEach((options, plannerItemId) => {
      options.forEach((option) => {
        if (option.catalogId && option.catalogId !== NON_SIZED_OPTION_ID) {
          map.set(option.catalogId, plannerItemId);
        }
      });
    });
    return map;
  }, [sizeOptionsByItemId]);

  const allSizeOptions = useMemo(() => {
    const combined: IssuePlannerSizeOption[] = [];
    sizeOptionsByParentId.forEach((options) => combined.push(...options));
    return combined;
  }, [sizeOptionsByParentId]);

  const sizeOptionsByCatalogId = useMemo(() => {
    const map = new Map<string, IssuePlannerSizeOption>();
    allSizeOptions.forEach((option) => {
      if (option.catalogId) {
        map.set(option.catalogId, option);
      }
    });
    return map;
  }, [allSizeOptions]);

  const sizeRequiredItemIds = useMemo(() => {
    return new Set<string>(Array.from(sizeOptionsByItemId.keys()));
  }, [sizeOptionsByItemId]);

  const isSizeSelectionsLoading = shouldShowIssueSizeLoadingSkeleton({
    hasSizedItems: ieParentCatalogIds.length > 0,
    isCatalogChildrenLoading,
    isSizeAvailabilityLoading,
  });

  // Build a map from plannerItemId → selectedCatalogId for items already in the cart.
  // Used to pre-populate size selections when resuming a saved/reserved-stock cart.
  const cartSizeByCatalogKey = useMemo(() => {
    const map = new Map<string, string>();
    ieCartMap.forEach((cartItem, plannerItemId) => {
      const selectedCatalogId = cartItem.selectedCatalogId?.trim();
      if (plannerItemId && selectedCatalogId) {
        map.set(plannerItemId, selectedCatalogId);
      }
    });
    return map;
  }, [ieCartMap]);

  // Build a map from plannerItemId → location_id derived from the first reserved asset.
  // Used to pre-populate the location dropdown when resuming a saved/reserved-stock cart
  // so the row shows where the reserved items actually live, not the catalog default.
  const cartLocationByPlannerItemId = useMemo(() => {
    const map = new Map<string, string>();
    ieCartMap.forEach((cartItem, plannerItemId) => {
      if (!plannerItemId || map.has(plannerItemId)) return;
      // Pull location_id from the first reserved asset in this group
      const firstAsset = dbCart.assets.find((a) => cartItem.assetIds.includes(a.id));
      const locId = typeof firstAsset?.entity_data?.location_id === 'string'
        ? firstAsset.entity_data.location_id.trim()
        : null;
      if (locId) map.set(plannerItemId, locId);
    });
    return map;
  }, [ieCartMap, dbCart.assets]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setSizeSelections((previous) => {
      let nextState = previous;
      let changed = false;
      const validItemIds = new Set(basePlannerItems.map((item) => item.id));

      Object.keys(previous).forEach((itemId) => {
        if (!validItemIds.has(itemId)) {
          if (!changed) {
            nextState = { ...nextState };
            changed = true;
          }
          delete nextState[itemId];
        }
      });

      basePlannerItems.forEach((item) => {
        const parentId = item.catalogId?.trim();
        if (!parentId) {
          return;
        }

        // For saved/reserved-stock carts, prefer the size already reserved in
        // the cart over the person's sizing profile so the planner row matches
        // the actual reservation.
        const cartCatalogId = cartSizeByCatalogKey.get(item.id);

        // Skip when already set, unless the cart has a more authoritative size
        // (cartCatalogId) that differs from what was previously defaulted.
        if (nextState[item.id] && !cartCatalogId) {
          return;
        }

        const defaultCatalogId = cartCatalogId ?? sizingByParentCatalogId.get(parentId);
        if (!defaultCatalogId) {
          return;
        }

        if (!changed) {
          nextState = { ...nextState };
          changed = true;
        }

        nextState[item.id] = defaultCatalogId;
      });

      return changed ? nextState : previous;
    });
  }, [open, basePlannerItems, sizingByParentCatalogId, cartSizeByCatalogKey]);

  // For saved/reserved-stock carts, pre-populate the location dropdown with the
  // location where the reserved assets actually live so the row is consistent
  // with what's already in the cart.
  useEffect(() => {
    if (!open || cartLocationByPlannerItemId.size === 0) return;
    setSelectedLocationByItemId((prev) => {
      let nextState = prev;
      let changed = false;
      cartLocationByPlannerItemId.forEach((locId, plannerItemId) => {
        if (nextState[plannerItemId] === locId) return;
        if (!changed) {
          nextState = { ...nextState };
          changed = true;
        }
        nextState[plannerItemId] = locId;
      });
      return changed ? nextState : prev;
    });
  }, [open, cartLocationByPlannerItemId]);

  const searchFilteredPlannerItems = useMemo(() => {
    const normalized = debouncedSearch.trim().toLowerCase();
    if (!normalized) {
      return plannerItems;
    }

    return plannerItems.filter((item) => {
      const candidates = [item.item, item.lin, item.nsn];
      return candidates.some((candidate) => (candidate ?? '').toLowerCase().includes(normalized));
    });
  }, [plannerItems, debouncedSearch]);

  const kitFilteredPlannerItems = useMemo(() => {
    if (selectedKit === 'all') {
      return searchFilteredPlannerItems;
    }

    return searchFilteredPlannerItems.filter((item) =>
      kitNamesByItemId.get(item.id)?.has(selectedKit),
    );
  }, [searchFilteredPlannerItems, selectedKit, kitNamesByItemId]);

  const plannerMap = useMemo(() => {
    const map = new Map<string, IssuePlannerItem>();
    plannerItems.forEach((entry) => map.set(entry.id, entry));
    return map;
  }, [plannerItems]);

  const filteredPlannerItems = useMemo(() => {
    const baseItems = kitFilteredPlannerItems;
    const sortedItems = [...baseItems].sort((a, b) => {
      const left = (a.item ?? '').trim();
      const right = (b.item ?? '').trim();
      const nameCompare = left.localeCompare(right, undefined, { sensitivity: 'base' });
      if (nameCompare !== 0) {
        return nameCompare;
      }
      return (a.lin ?? '').localeCompare(b.lin ?? '', undefined, { sensitivity: 'base' });
    });

    if (view === 'missing') {
      return sortedItems.filter((item) => {
        // Shortages view should reflect *actual* shortages based on HR assignments only.
        // Items reserved in a cart are not yet issued and should not affect shortages.
        return item.need > 0;
      });
    }
    return sortedItems;
  }, [view, kitFilteredPlannerItems]);

  const catalogIdsWithSizedOptions = useMemo(() => {
    const set = new Set<string>();
    sizeOptionsByCatalogId.forEach((_option, catalogId) => {
      if (catalogId && catalogId !== NON_SIZED_OPTION_ID) {
        set.add(catalogId);
      }
    });
    return Array.from(set);
  }, [sizeOptionsByCatalogId]);

  const reservedByCatalogId = useMemo(() => {
    const map = new Map<string, number>();
    ieCartItems.forEach((item) => {
      if (!item.selectedCatalogId) {
        return;
      }
      map.set(
        item.selectedCatalogId,
        (map.get(item.selectedCatalogId) ?? 0) + item.assetIds.length,
      );
    });
    return map;
  }, [ieCartItems]);

  const sizeAvailabilityByCatalogId = useMemo(() => {
    const result: Record<string, number> = {};
    Object.entries(sizeAvailabilityBaseline).forEach(([catalogId, baseline]) => {
      const reserved = reservedByCatalogId.get(catalogId) ?? 0;
      // baseline is already "available" (unreserved) from iem_available_asset_counts,
      // so we must not subtract reserved again. For saved-cart resume views we add
      // back this cart's reserved quantity so rows reflect what the user already holds.
      result[catalogId] = includeOwnReservedInAvailability
        ? Math.max(baseline + reserved, 0)
        : Math.max(baseline, 0);
    });
    return result;
  }, [includeOwnReservedInAvailability, sizeAvailabilityBaseline, reservedByCatalogId]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (catalogIdsWithSizedOptions.length === 0) {
      setSizeAvailabilityBaseline({});
      setIsSizeAvailabilityLoading(false);
      return;
    }

    let isCancelled = false;
    setIsSizeAvailabilityLoading(true);

    const loadAvailabilityByCatalogIds = async () => {
      try {
        const counts: Record<string, Record<string, number>> = await iemService.getAvailableAssetCountsByCatalogIds({
          catalogIds: catalogIdsWithSizedOptions,
        });

        if (isCancelled) {
          return;
        }

        const nextAvailability: Record<string, number> = {};

        for (const [catalogId, locationCounts] of Object.entries(counts)) {
          if (sizeOptionsByCatalogId.has(catalogId)) {
            // Sum only located assets — assets without a location cannot be issued
            let total = 0;
            for (const [key, count] of Object.entries(locationCounts as Record<string, number>)) {
              if (key !== '_total') {
                total += count;
              }
            }
            nextAvailability[catalogId] = total;
          }
        }

        sizeOptionsByCatalogId.forEach((_option, catalogId) => {
          if (!(catalogId in nextAvailability)) {
            nextAvailability[catalogId] = 0;
          }
        });
        setSizeAvailabilityBaseline(nextAvailability);
        setIsSizeAvailabilityLoading(false);
      } catch (error) {
        log.error('Failed to load availability for catalog IDs', {
          catalogIds: catalogIdsWithSizedOptions,
          error: String(error),
        });
        if (!isCancelled) {
          setIsSizeAvailabilityLoading(false);
        }
      }
    };

    void loadAvailabilityByCatalogIds();

    return () => {
      isCancelled = true;
    };
  }, [open, catalogIdsWithSizedOptions, sizeOptionsByCatalogId, refreshTrigger]);

  const handleSelectSize = useCallback((itemId: string, catalogId?: string) => {
    setSizeSelections((previous) => ({
      ...previous,
      [itemId]: catalogId && catalogId.trim().length > 0 ? catalogId : undefined,
    }));
  }, []);

  const handleQuantityChange = useCallback((itemId: string, quantity: number) => {
    setQuantitySelections((previous) => ({
      ...previous,
      [itemId]: quantity,
    }));
  }, []);

  const fetchAndAssignAssets = useCallback(
    async (
      plannerItem: IssuePlannerItem,
      options?: { selectedCatalogId?: string | null },
    ): Promise<boolean> => {
      const count = plannerItem.quantityToIssue ?? plannerItem.need;
      if (count <= 0) {
        return false;
      }

      // Check for size requirements
      const requiresSizeSelection = sizeRequiredItemIds.has(plannerItem.id);
      const selectedCatalogId = options?.selectedCatalogId ?? sizeSelections[plannerItem.id];
      const sizeOptions = sizeOptionsByItemId.get(plannerItem.id);
      const isNonSizedSelection = selectedCatalogId === NON_SIZED_OPTION_ID;
      const hasValidSizeSelection =
        !!selectedCatalogId &&
        !isNonSizedSelection &&
        sizeOptions?.some((option) => option.catalogId === selectedCatalogId);

      if (requiresSizeSelection && (isNonSizedSelection || !hasValidSizeSelection)) {
        // No size selected for an item that has size options. Treat this as a
        // parent-level request for the mobile workflow instead of blocking the
        // user. The placeholder records intent to issue this parent catalog
        // item without reserving concrete stock yet.
        if (!customerId || !plannerItem.catalogId) {
          notifications.warning(
            'Select a size',
            `Choose a size for ${plannerItem.item} before adding it to the cart.`,
          );
          return false;
        }

        try {
          const result = await upsertSizeAllocationMutation.mutateAsync({
            customerId,
            parentCatalogId: plannerItem.catalogId,
            quantity: count,
          });

          if (!result.success) {
            notifications.error(
              'Failed to add placeholder',
              result.error ?? 'Unable to save size allocation.',
            );
            return false;
          }

          return true;
        } catch (error) {
          const message = extractErrorMessage(error, 'Unable to save size allocation.');
          notifications.error('Failed to add placeholder', message);
          return false;
        }
      }

      const resolvedCatalogId = hasValidSizeSelection ? selectedCatalogId : plannerItem.catalogId;
      const selectedSizeOption =
        resolvedCatalogId && sizeOptions
          ? sizeOptions.find((option) => option.catalogId === resolvedCatalogId)
          : null;
      const selectedSizeLabel = selectedSizeOption?.sizeLabel ?? null;

      const allocateResult = await allocateAssets({
        catalogId: resolvedCatalogId,
        itemName: plannerItem.item,
        quantity: count,
        sizeLabel: selectedSizeLabel,
        ieKitId: plannerItem.ieKitId,
        locationId: resolveLocationForItem(plannerItem.id, plannerItem.catalogId, resolvedCatalogId),
      });

      // If sized assets were successfully allocated, clear any existing placeholder
      // for this parent catalog. This ensures the "Planned" count is updated correctly
      // when transitioning from unsized to sized items.
      if (allocateResult && customerId && plannerItem.catalogId) {
        await upsertSizeAllocationMutation.mutateAsync({
          customerId,
          parentCatalogId: plannerItem.catalogId,
          quantity: 0,
        });
      }

      return allocateResult;
    },
    [
      allocateAssets,
      customerId,
      resolveLocationForItem,
      notifications,
      sizeOptionsByItemId,
      sizeRequiredItemIds,
      sizeSelections,
      upsertSizeAllocationMutation,
    ],
  );

  // Build a catalog-level cart quantity map as a fallback for when the planner
  // item ID lookup fails (e.g. sized items whose child catalog ID doesn't resolve
  // back to the parent planner key).
  const cartQtyByCatalogId = useMemo(() => {
    const map = new Map<string, number>();
    for (const cartItem of ieCartMap.values()) {
      const catalogId = cartItem.selectedCatalogId?.trim();
      if (!catalogId) continue;
      // Resolve child catalog → parent catalog for sized items
      const parentId = parentCatalogIdByChildCatalogIdEarly.get(catalogId) ?? catalogId;
      map.set(parentId, (map.get(parentId) ?? 0) + cartItem.assetIds.length);
    }
    return map;
  }, [ieCartMap, parentCatalogIdByChildCatalogIdEarly]);

  const reserveStockPlan = useMemo(() => {
    return buildReserveStockPlan(
      filteredPlannerItems.map((item) => {
        const directCartQty = ieCartMap.get(item.id)?.assetIds.length ?? 0;
        // Fall back to catalog-level count when the planner-key lookup misses
        const cartQty =
          directCartQty > 0
            ? directCartQty
            : (item.catalogId ? (cartQtyByCatalogId.get(item.catalogId) ?? 0) : 0);
        const rawSelection = sizeSelections[item.id];
        const selectedCatalogId =
          rawSelection && rawSelection !== NON_SIZED_OPTION_ID ? rawSelection : null;
        const requiresSize = sizeRequiredItemIds.has(item.id);
        const availability =
          requiresSize && selectedCatalogId
            ? sizeAvailabilityByCatalogId[selectedCatalogId]
            : undefined;

        const sizeOptions = sizeOptionsByItemId.get(item.id);
        const allSizesUnavailable =
          requiresSize &&
          !selectedCatalogId &&
          Boolean(sizeOptions) &&
          sizeOptions!
            .filter((option) => option.catalogId !== NON_SIZED_OPTION_ID)
            .every((option) => {
              const qty = sizeAvailabilityByCatalogId[option.catalogId];
              return typeof qty === 'number' && qty <= 0;
            });

        return {
          id: item.id,
          itemName: item.item,
          lin: item.lin,
          catalogId: item.catalogId,
          ieKitId: item.ieKitId,
          need: item.need,
          reservedQty: cartQty,
          existingPlaceholderQty: item.catalogId
            ? (plannedByCatalogId.get(item.catalogId) ?? 0)
            : 0,
          requiresSize,
          selectedCatalogId,
          maxAllocatableQty: availability,
          allSizesUnavailable,
        };
      }),
      (catalogId, plannerItemId) => {
        // For sized items, catalogId is the child (size variant); fall back to parent for lookups.
        const parentCatalogId = parentCatalogIdByChildCatalogIdEarly.get(catalogId) ?? catalogId;
        return resolveLocationForItem(plannerItemId, parentCatalogId, catalogId);
      },
    );
  }, [
    filteredPlannerItems,
    ieCartMap,
    cartQtyByCatalogId,
    parentCatalogIdByChildCatalogIdEarly,
    sizeSelections,
    sizeRequiredItemIds,
    sizeAvailabilityByCatalogId,
    sizeOptionsByItemId,
    plannedByCatalogId,
    resolveLocationForItem,
  ]);

  const hasAddableItems =
    reserveStockPlan.allocations.length > 0 || reserveStockPlan.placeholders.length > 0;
  const isBulkProcessing = isBulkAdding;

  const handleAddIeItem = useCallback(
    async (plannerItem: IssuePlannerItem, options?: { availabilityMap?: Map<string, number> }) => {
      if (readOnly) return;
      const existing = ieCartMap.get(plannerItem.id);
      const assignedCount = existing?.assetIds.length ?? 0;
      const remainingNeed = Math.max(plannerItem.need - assignedCount, 0);
      let amountToAdd = plannerItem.quantityToIssue || remainingNeed;

      if (amountToAdd > remainingNeed) {
        amountToAdd = remainingNeed;
      }

      const dueRemaining = amountToAdd;

      if (dueRemaining <= 0) {
        return;
      }

      const requiresSizeSelection = sizeRequiredItemIds.has(plannerItem.id);

      setPlannerActionId(plannerItem.id);
      try {
        if (requiresSizeSelection) {
          // Skip cache check - let fetchAndAssignAssets do a fresh server check
          // The cached availability is used only for UI display purposes
          const success = await fetchAndAssignAssets({
            ...plannerItem,
            quantityToIssue: dueRemaining,
          });
          if (success) {
            setQuantitySelections((prev) => ({ ...prev, [plannerItem.id]: undefined }));
          }
          return;
        }

        let issuableCount = dueRemaining;
        const normalizedNsn = plannerItem.nsn?.trim();

        if (normalizedNsn && normalizedNsn !== '—') {
          const availabilityMap = options?.availabilityMap;
          let onHand: number | undefined = availabilityMap?.get(normalizedNsn);

          if (onHand === undefined) {
            try {
              const availability = await iemService.checkAvailability([
                { nsn: normalizedNsn, qty: dueRemaining },
              ]);
              const match = availability.results.find((entry) => entry.nsn === normalizedNsn);
              onHand = Math.max(match?.on_hand ?? 0, 0);
            } catch (error) {
              const message = extractErrorMessage(error, 'Unable to verify available inventory.');
              notifications.error('Availability check failed', message);
              return;
            }
          }

          const normalizedOnHand = Math.max(onHand ?? 0, 0);
          issuableCount = Math.min(dueRemaining, normalizedOnHand);

          if (availabilityMap && onHand !== undefined) {
            availabilityMap.set(normalizedNsn, Math.max(normalizedOnHand - issuableCount, 0));
          }
        }

        if (issuableCount <= 0) {
          notifications.warning(
            'No available assets',
            `No available inventory found for ${plannerItem.item}.`,
          );
          return;
        }

        if (issuableCount < dueRemaining) {
          notifications.warning(
            'Limited inventory',
            `Only ${issuableCount} asset${issuableCount === 1 ? '' : 's'} available for ${plannerItem.item}.`,
          );
        }

        const success = await fetchAndAssignAssets({
          ...plannerItem,
          quantityToIssue: issuableCount,
        });
        if (success) {
          setQuantitySelections((prev) => ({ ...prev, [plannerItem.id]: undefined }));
        }
      } finally {
        setPlannerActionId(null);
      }
    },
    [ieCartMap, fetchAndAssignAssets, notifications, sizeRequiredItemIds, readOnly],
  );

  const getMatchingCartItems = useCallback(
    (itemId: string) => {
      const matches = new Map<string, IssuePlannerCartItem>();

      ieCartItems.forEach((item) => {
        if (
          item.id === itemId ||
          item.plannerItemId === itemId ||
          resolvePlannerItemIdForCartItem(item) === itemId
        ) {
          matches.set(item.id, item);
        }
      });

      const catalogId = catalogIdByPlannerId.get(itemId);
      if (catalogId && primaryPlannerIdByCatalogId.get(catalogId) === itemId) {
        ieCartItems.forEach((item) => {
          if (!item.ieKitId && item.selectedCatalogId === catalogId) {
            matches.set(item.id, item);
          }
        });
      }

      return Array.from(matches.values());
    },
    [
      ieCartItems,
      catalogIdByPlannerId,
      primaryPlannerIdByCatalogId,
      resolvePlannerItemIdForCartItem,
    ],
  );

  const handleRemoveIeItem = useCallback(
    async (itemId: string) => {
      if (readOnly) return;
      // Find the cart item(s) to remove - could be by id or plannerItemId
      const itemsToRemove = getMatchingCartItems(itemId);

      // Collect all asset IDs from items to remove
      const allAssetIds = itemsToRemove.flatMap((item) => item.assetIds);
      if (allAssetIds.length > 0) {
        await dbCart.removeFromCart(allAssetIds);
      }
    },
    [dbCart, getMatchingCartItems, readOnly],
  );

  const handleAddAllAvailable = useCallback(async (options?: { silent?: boolean }) => {
    if (readOnly) return;
    if (isBulkProcessing || plannerActionId) return;
    if (!customerId) return;

    const targetPlaceholderParentIds = new Set(
      reserveStockPlan.placeholders.map((placeholder) => placeholder.parentCatalogId),
    );
    const relevantParentCatalogIds = new Set<string>();
    filteredPlannerItems.forEach((item) => {
      const parentCatalogId = item.catalogId?.trim();
      if (parentCatalogId) {
        relevantParentCatalogIds.add(parentCatalogId);
      }
    });
    const stalePlaceholderParentIds = Array.from(relevantParentCatalogIds).filter(
      (parentCatalogId) => {
        const existingQty = plannedByCatalogId.get(parentCatalogId) ?? 0;
        return existingQty > 0 && !targetPlaceholderParentIds.has(parentCatalogId);
      },
    );

    if (
      reserveStockPlan.allocations.length === 0 &&
      reserveStockPlan.placeholders.length === 0 &&
      stalePlaceholderParentIds.length === 0
    ) {
      return;
    }

    // Build bulk allocation items from addable planner items (sized items with stock)
    const bulkItems = reserveStockPlan.allocations;

    setIsBulkAdding(true);
    try {
      let allocatedCount = 0;
      let placeholderCount = 0;

      // Allocate sized items with available stock
      if (bulkItems.length > 0) {
        const result = await dbCart.bulkAllocateToCart(bulkItems, { silent: options?.silent });
        if (result && result.success) {
          allocatedCount = result.allocated_count ?? 0;
        } else if (result && !result.success) {
          notifications.error('Failed to add items', result.error ?? 'Unknown error');
        }
      }

      // Create placeholders for unsized items (no size selected) - bulk operation
      if (reserveStockPlan.placeholders.length > 0) {
        const placeholderResult = await cartService.bulkUpsertSizeAllocationCart({
          customerId,
          items: reserveStockPlan.placeholders.map(({ parentCatalogId, quantity }) => ({
            parentCatalogId,
            quantity,
          })),
        });
        if (placeholderResult.success) {
          placeholderCount = reserveStockPlan.placeholders.reduce(
            (sum, item) => sum + item.added,
            0,
          );
        } else {
          notifications.error(
            'Failed to add items',
            placeholderResult.error ?? 'Unable to save size allocations.',
          );
        }
      }

      // Clear stale placeholders for parent catalogs that no longer need unsized requests.
      if (stalePlaceholderParentIds.length > 0) {
        await Promise.all(
          stalePlaceholderParentIds.map((parentCatalogId) =>
            upsertSizeAllocationMutation.mutateAsync({
              customerId,
              parentCatalogId,
              quantity: 0,
            }),
          ),
        );
      }

      // Always refetch the cart after allocation to ensure ieCartMap is current
      // before re-enabling the button (prevents duplicate allocations on rapid clicks).
      await dbCart.refetch();

      // Show notification if we added anything
      if (allocatedCount > 0 || placeholderCount > 0) {
        const parts: string[] = [];
        if (allocatedCount > 0) {
          parts.push(`${allocatedCount} reserved`);
        }
        if (placeholderCount > 0) {
          parts.push(`${placeholderCount} unsized`);
        }
        notifications.success('Added to cart', parts.join(', '));
      }

      if (reserveStockPlan.missingDueToStock > 0) {
        notifications.warning(
          'Some items skipped',
          `${reserveStockPlan.missingDueToStock} item(s) could not be added because no sizes are currently available.`,
        );
      }
    } catch (error) {
      const message = extractErrorMessage(error, 'Unable to allocate assets.');
      notifications.error('Failed to add items', message);
    } finally {
      setIsBulkAdding(false);
    }
  }, [
    readOnly,
    isBulkProcessing,
    plannerActionId,
    customerId,
    reserveStockPlan,
    filteredPlannerItems,
    plannedByCatalogId,
    dbCart,
    notifications,
    upsertSizeAllocationMutation,
  ]);

  const handleAdjustIeQuantity = useCallback(
    async (itemId: string, delta: number) => {
      if (readOnly) return;
      if (delta === 0) {
        return;
      }

      if (delta > 0) {
        const cartItem = ieCartItems.find(
          (item) =>
            item.id === itemId ||
            item.plannerItemId === itemId ||
            resolvePlannerItemIdForCartItem(item) === itemId,
        );
        const candidatePlannerIds = [
          itemId,
          cartItem?.plannerItemId,
          cartItem ? resolvePlannerItemIdForCartItem(cartItem) : undefined,
          cartItem?.selectedCatalogId
            ? sizeParentByCatalogId.get(cartItem.selectedCatalogId)
            : undefined,
        ].filter((value): value is string => Boolean(value));
        const resolvedPlannerItemId =
          candidatePlannerIds.find((candidate) => plannerMap.has(candidate)) ?? null;
        const plannerItem = resolvedPlannerItemId ? plannerMap.get(resolvedPlannerItemId) : null;
        if (!plannerItem) {
          const warningTitle = 'Unable to adjust quantity';
          // Prevent duplicate warnings
          const alreadyShowing = activeNotifications.some(
            (n) => n.type === 'warning' && n.title === warningTitle,
          );

          if (!alreadyShowing) {
            notifications.warning(warningTitle, 'Planner item is no longer available.');
          }
          return;
        }

        const assignedCount = getMatchingCartItems(resolvedPlannerItemId ?? itemId).reduce(
          (sum, entry) => sum + entry.assetIds.length,
          0,
        );
        if (assignedCount >= plannerItem.need) {
          const warningTitle = 'Cannot issue more than authorized quantity.';
          const alreadyShowing = activeNotifications.some(
            (n) => n.type === 'warning' && n.title === warningTitle,
          );

          if (!alreadyShowing) {
            notifications.warning(warningTitle);
          }
          return;
        }

        setPlannerActionId(itemId);
        try {
          // Increase the quantityToIssue by delta
          const currentQty = plannerItem.quantityToIssue ?? 0;
          const newQty = Math.min(currentQty + delta, plannerItem.need);
          setQuantitySelections((prev) => ({ ...prev, [plannerItem.id]: newQty }));
          const success = await fetchAndAssignAssets(
            { ...plannerItem, quantityToIssue: delta },
            cartItem?.selectedCatalogId
              ? { selectedCatalogId: cartItem.selectedCatalogId }
              : undefined,
          );
          if (success) {
            setQuantitySelections((prev) => ({ ...prev, [plannerItem.id]: undefined }));
          }
        } finally {
          setPlannerActionId(null);
        }
        return;
      }

      // Decrement logic: Remove assets from matching cart items via database
      let amountToRemove = Math.abs(delta);
      const matchingItems = getMatchingCartItems(itemId);

      const assetsToRemove: string[] = [];
      for (const item of matchingItems) {
        if (amountToRemove <= 0) break;

        const count = item.assetIds.length;
        if (count <= amountToRemove) {
          // Remove all assets from this item
          assetsToRemove.push(...item.assetIds);
          amountToRemove -= count;
        } else {
          // Partial removal - remove last N assets
          assetsToRemove.push(...item.assetIds.slice(count - amountToRemove));
          amountToRemove = 0;
        }
      }

      if (assetsToRemove.length > 0) {
        await dbCart.removeFromCart(assetsToRemove);
      }
    },
    [
      sizeParentByCatalogId,
      ieCartItems,
      getMatchingCartItems,
      dbCart,
      fetchAndAssignAssets,
      notifications,
      plannerMap,
      readOnly,
      activeNotifications,
      resolvePlannerItemIdForCartItem,
    ],
  );

  const toolbar = useMemo(() => {
    return (
      <IssuePlannerToolbar
        view={view}
        onViewChange={(nextView) => setView(nextView)}
        search={search}
        onSearchChange={setSearch}
        searchLabel={`Search ${ieKitLabel} items`}
        searchPlaceholder="Search by nomenclature, LIN, or NSN"
        selectedKit={selectedKit}
        onKitChange={setSelectedKit}
        availableKits={uniqueKitNames}
      />
    );
  }, [view, search, selectedKit, uniqueKitNames]);

  const content = useMemo(() => {
    // Build location options keyed by planner item ID
    // Use selected size (child) catalog ID when available, fall back to parent
    const plannerLocationOptions = new Map<string, LocationOption[]>();
    filteredPlannerItems.forEach((item) => {
      const selectedSizeCatalogId = sizeSelections[item.id]?.trim();
      const effectiveCatalogId = (selectedSizeCatalogId && selectedSizeCatalogId !== NON_SIZED_OPTION_ID)
        ? selectedSizeCatalogId
        : item.catalogId?.trim();
      if (effectiveCatalogId) {
        plannerLocationOptions.set(item.id, getLocationOptionsForCatalogId(effectiveCatalogId));
      }
    });

    return (
      <IssuePlannerTable
        customerId={customerId}
        isLoading={isLoading}
        plannerItems={filteredPlannerItems}
        ieCartMap={ieCartMap}
        plannedRequestCounts={plannedRequestCounts}
        plannerActionId={plannerActionId}
        isBulkProcessing={isBulkProcessing}
        readOnly={readOnly}
        sizeOptionsByItemId={sizeOptionsByItemId}
        sizeSelections={sizeSelections}
        isSizeSelectionsLoading={isSizeSelectionsLoading}
        sizeAvailabilityByCatalogId={sizeAvailabilityByCatalogId}
        onSizeChange={handleSelectSize}
        onQuantityChange={handleQuantityChange}
        onAddPlannerItem={(item) => handleAddIeItem(item)}
        onRemovePlannerItem={handleRemoveIeItem}
        locationOptionsByItemId={plannerLocationOptions}
        selectedLocationByItemId={selectedLocationByItemId}
        onLocationChange={handleLocationChange}
        locationsLoading={isChildEntitiesLoading}
        preferBestQuantityLocationDefault={preferBestQuantityLocationDefault}
      />
    );
  }, [
    customerId,
    filteredPlannerItems,
    ieCartMap,
    plannedRequestCounts,
    plannerActionId,
    isBulkProcessing,
    readOnly,
    handleAddIeItem,
    handleSelectSize,
    isSizeSelectionsLoading,
    sizeAvailabilityByCatalogId,
    handleRemoveIeItem,
    handleQuantityChange,
    isLoading,
    sizeOptionsByItemId,
    sizeSelections,
    getLocationOptionsForCatalogId,
    selectedLocationByItemId,
    handleLocationChange,
    isChildEntitiesLoading,
    preferBestQuantityLocationDefault,
  ]);

  const actions = useMemo<IssueIeTabActions>(
    () => ({
      addAllAvailable: handleAddAllAvailable,
      adjustQuantity: handleAdjustIeQuantity,
      removeItem: (itemId: string) => void handleRemoveIeItem(itemId),
      clearCart: () => {
        void dbCart.clearCart();
      },
    }),
    [dbCart, handleAddAllAvailable, handleAdjustIeQuantity, handleRemoveIeItem],
  );

  const prevReservedAssetIdsRef = useRef<string[]>([]);
  useEffect(() => {
    const prev = prevReservedAssetIdsRef.current;
    const changed =
      prev.length !== reservedAssetIds.length || reservedAssetIds.some((id, i) => prev[i] !== id);
    if (changed) {
      prevReservedAssetIdsRef.current = reservedAssetIds;
      onReservedAssetIdsChange?.(reservedAssetIds);
    }
  }, [onReservedAssetIdsChange, reservedAssetIds]);

  return {
    toolbar,
    content,
    cartItems: Array.from(ieCartMap.values()),
    plannerItems,
    hasAddableItems,
    plannerActionId,
    isBulkProcessing,
    actions,
    reservedAssetIds,
  };
};
